/* =========================================================================
   FIRST-TIME MINI-GAME INSTRUCTIONS — one shared overlay, four data sets.

   Pattern (PLAN-gameplay-round2.md Part 1): the REAL screen renders idle behind
   a dim layer; 2-3 numbered callouts each spotlight one real element (outline
   glow + a brightened backdrop — nothing is cloned, the live UI shows through)
   and carry ONE short caption. Footer "Tap anywhere to begin"; an ✕ top-right
   closes too; tapping anywhere dismisses from the very first frame — this is a
   "respect the player" hint layer, never a forced walkthrough.

   Lifecycle: shows AUTOMATICALLY on a player's first encounter with each game
   (`catholics.tutorialSeen.<gameKey>`, same localStorage family as
   `catholics.arcadeUnlocked`), and stays re-openable forever through the (i)
   button in each mini-game's kicker row. The seen key is deliberately NOT reset
   on logout — a returning player has not forgotten how the wheel works.

   Because both entry points (gameController's runBreak and arcade's playArcade)
   funnel through the same four showX() functions, hooking maybeShowTutorial()
   inside showX() covers the in-round break and the Arcade replay at once.
   ========================================================================= */
import { esc } from './engine';
import { track } from './telemetry';

export type TutorialGameKey = 'wheel' | 'match3' | 'marymatch3' | 'manna' | 'friendly';
export type ArrowDir = 'up' | 'down' | 'left' | 'right';

/** One instruction: a CSS selector into the REAL screen, its caption, and which
    way the arrow points at that element (the card sits on the opposite side). */
export interface Callout {
  target: string;
  text: string;
  arrow: ArrowDir;
}

/* Copy is authored per the plan (brand/STYLE-GUIDE.md §1 — premium, women 40+):
   one plain declarative sentence each, no exclamation-point energy. Max 4
   callouts per game by rule; these use 2-3. */
export const TUTORIALS: Record<TutorialGameKey, Callout[]> = {
  wheel: [
    {
      target: '#spinBtn',
      arrow: 'down',
      text: 'One tap spins the wheel — no aiming, no timing. Where it lands is yours.',
    },
    {
      // Matches all three item wedges (🎟️/📿/⚱️) at once — see minigames/wheel.ts
      // buildWheel(), which tags only those slices with this class.
      target: '.wheel-seg-item',
      arrow: 'down',
      text: 'These three are treasures, not dollars — they go to your inventory for quests and collections.',
    },
  ],
  match3: [
    { target: '#match3Board', arrow: 'down', text: 'Tap a gift, then tap its neighbor, to swap them.' },
    {
      target: '#match3Board',
      arrow: 'up',
      text: 'Line up 3 or more of a kind to clear them — new gifts fall from heaven.',
    },
    { target: '#match3Moves', arrow: 'up', text: '15 moves. Every gift you clear counts at the end.' },
  ],
  marymatch3: [
    {
      target: '#maryBoard',
      arrow: 'up',
      text: 'Swap like Faith Match — tap a symbol, then its neighbor, to line up 3+.',
    },
    {
      target: '#screenMaryMatch3 .manna-hud',
      arrow: 'up',
      text: "Three tiers of grace: reach each tier's Goal before its moves run out to advance to the next.",
    },
  ],
  manna: [
    {
      target: '#mannaField',
      arrow: 'up',
      text: 'Slide your finger anywhere across the field — the basket follows your touch.',
    },
    {
      target: '#mannaCount',
      arrow: 'up',
      text: "Catch 10 loaves. A quick basket earns more — but there's no losing here.",
    },
  ],
  friendly: [
    {
      // Matches every area's name across the whole picker — see friendlyController.ts
      // rowHTML(), which renders one .fr-name per study area.
      target: '.fr-name',
      arrow: 'down',
      text: 'Pick a Type — a subject to study, like Scripture or Doctrine.',
    },
    {
      // ONLY the first row's chips (2026-08-04, Eric: testers overlooked this step).
      // Matching every '.fr-level' across every area — a long, scrollable list —
      // made the spot's union bounding box stretch to the LAST row, and an 'up'
      // arrow places the card BELOW that box: off-screen on any real catalog.
      // Scoping to one row keeps the spot compact and near the top, like step 1.
      target: '.fr-row:first-child .fr-level',
      arrow: 'up',
      text: 'Then pick a Complexity. Tap a chip to start that set of questions.',
    },
  ],
};

/* ------------------------------ seen tracking ---------------------------- */

const SEEN_PREFIX = 'catholics.tutorialSeen.';

export function tutorialSeenKey(game: TutorialGameKey): string {
  return SEEN_PREFIX + game;
}

/** localStorage can throw in private/locked-down contexts — every touch guarded
    (same seam as arcade.ts). A failure degrades to "not seen", never a crash. */
export function hasSeenTutorial(game: TutorialGameKey): boolean {
  try {
    return localStorage.getItem(tutorialSeenKey(game)) === '1';
  } catch {
    return false;
  }
}

export function markTutorialSeen(game: TutorialGameKey): void {
  try {
    localStorage.setItem(tutorialSeenKey(game), '1');
  } catch {
    /* private mode — the overlay simply greets them again next time */
  }
}

/** Test/QA hygiene only. NOT called on logout by design (see header). */
export function resetTutorialSeen(game: TutorialGameKey): void {
  try {
    localStorage.removeItem(tutorialSeenKey(game));
  } catch {
    /* noop */
  }
}

/** The callouts whose target actually exists on the mounted screen. A missing
    element (markup drifted) silently drops that one callout instead of
    breaking the mini-game. */
export function resolveCallouts(game: TutorialGameKey): Callout[] {
  const list = TUTORIALS[game] || [];
  if (typeof document === 'undefined') return [];
  return list.filter((c) => {
    try {
      return document.querySelector(c.target) !== null;
    } catch {
      return false;
    }
  });
}

/* --------------------------------- markup -------------------------------- */

export const tutorialOverlayHTML = /* html */ `
<div id="tutorialOverlay" class="tutorial-overlay hidden" aria-hidden="true" role="dialog" aria-label="How to play">
  <div class="tut-dim"></div>
  <div class="tut-layer" id="tutorialLayer"></div>
  <button class="tut-close" id="tutorialClose" type="button" aria-label="Close instructions">✕</button>
  <div class="tut-foot">Tap anywhere to begin</div>
</div>`;

/** The small (i) that re-opens a game's instructions, dropped into its kicker
    row. `data-tut` is what initTutorials() delegates on. */
export function tutorialInfoButtonHTML(game: TutorialGameKey): string {
  return `<button class="tut-info" type="button" data-tut="${game}" aria-label="How to play">i</button>`;
}

/* -------------------------------- rendering ------------------------------- */

const THEO_ART = 'media/characters/theo-teach.png';
let theoArtAvail = false;

/** Probe the optional Theo accent exactly like characters/ui.ts does — never a
    hardcoded <img> that could 404 if the art has not shipped to this build. */
function probeTheoArt(): void {
  try {
    const img = new Image();
    img.onload = () => {
      theoArtAvail = true;
    };
    img.onerror = () => {
      /* no art — the numbered chip stands alone */
    };
    img.src = THEO_ART;
  } catch {
    /* no Image() (non-browser) — accent stays off */
  }
}

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

const GAP = 12; // px between a spotlight and its card
const EDGE = 10; // px minimum margin from the overlay edge
const SPOT_PAD = 6; // px the glow ring stands off the real element

/** Keep a value inside [lo, hi] (hi < lo degrades to lo — tiny screens). */
function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(Math.max(lo, hi), v));
}

function overlaps(a: Box, b: Box): boolean {
  return (
    a.left < b.left + b.width && a.left + a.width > b.left && a.top < b.top + b.height && a.top + a.height > b.top
  );
}

/** Place one card next to its spotlight, then nudge it clear of cards already
    placed. Layout only — safe to no-op under jsdom (all rects are zero). */
function placeCard(card: HTMLElement, arrowEl: HTMLElement, spot: Box, arrow: ArrowDir, ov: Box, placed: Box[]): void {
  const w = card.offsetWidth || 0;
  const h = card.offsetHeight || 0;
  const spotCx = spot.left + spot.width / 2;
  const spotCy = spot.top + spot.height / 2;
  let left: number;
  let top: number;
  if (arrow === 'down') {
    left = spotCx - w / 2;
    top = spot.top - GAP - h;
  } else if (arrow === 'up') {
    left = spotCx - w / 2;
    top = spot.top + spot.height + GAP;
  } else if (arrow === 'right') {
    left = spot.left - GAP - w;
    top = spotCy - h / 2;
  } else {
    left = spot.left + spot.width + GAP;
    top = spotCy - h / 2;
  }
  left = clamp(left, EDGE, ov.width - w - EDGE);
  top = clamp(top, EDGE, ov.height - h - EDGE);

  // De-overlap: cards are small; push a colliding one below the last thing it
  // hit, and if that runs off the bottom, above it instead.
  for (const p of placed) {
    if (!overlaps({ left, top, width: w, height: h }, p)) continue;
    const below = p.top + p.height + 8;
    top = below + h + EDGE <= ov.height ? below : Math.max(EDGE, p.top - h - 8);
  }
  card.style.left = Math.round(left) + 'px';
  card.style.top = Math.round(top) + 'px';
  placed.push({ left, top, width: w, height: h });

  // Point the arrow at the target even after the card was clamped/nudged.
  if (arrow === 'up' || arrow === 'down') {
    arrowEl.style.left = Math.round(clamp(spotCx - left, 16, Math.max(16, w - 16))) + 'px';
  } else {
    arrowEl.style.top = Math.round(clamp(spotCy - top, 16, Math.max(16, h - 16))) + 'px';
  }
}

function buildCallout(c: Callout, n: number, first: boolean): { card: HTMLElement; arrow: HTMLElement } {
  const card = document.createElement('div');
  card.className = 'tut-card';
  const arrow = document.createElement('span');
  arrow.className = 'tut-arrow arrow-' + c.arrow;
  card.appendChild(arrow);
  const theo = first && theoArtAvail ? `<img class="tut-theo" src="${THEO_ART}" alt="" />` : '';
  const body = document.createElement('div');
  body.className = 'tut-card-body';
  body.innerHTML = `${theo}<span class="tut-num">${n}</span><span class="tut-text">${esc(c.text)}</span>`;
  card.appendChild(body);
  return { card, arrow };
}

let openGame: TutorialGameKey | null = null;

/** Paint the dim + spotlights + cards for the currently open game. Re-runnable
    (resize / orientation change) — it rebuilds the layer from scratch. */
function paint(game: TutorialGameKey): void {
  const ov = document.getElementById('tutorialOverlay');
  const layer = document.getElementById('tutorialLayer');
  if (!ov || !layer) return;
  layer.innerHTML = '';
  const ovr = ov.getBoundingClientRect();
  const bounds: Box = { left: 0, top: 0, width: ovr.width, height: ovr.height };
  const placed: Box[] = [];
  const pending: { card: HTMLElement; arrow: HTMLElement; spot: Box; dir: ArrowDir }[] = [];

  resolveCallouts(game).forEach((c, i) => {
    // A target can match MORE than one element (e.g. the wheel's three item
    // wedges) — spotlight every match individually, then anchor the card/arrow
    // to their union so multi-element callouts place exactly like single ones.
    const els = Array.from(document.querySelectorAll(c.target)) as HTMLElement[];
    if (els.length === 0) return;
    const rects = els.map((el) => el.getBoundingClientRect());
    const left = Math.min(...rects.map((r) => r.left));
    const top = Math.min(...rects.map((r) => r.top));
    const right = Math.max(...rects.map((r) => r.right));
    const bottom = Math.max(...rects.map((r) => r.bottom));
    const spot: Box = {
      left: left - ovr.left - SPOT_PAD,
      top: top - ovr.top - SPOT_PAD,
      width: right - left + SPOT_PAD * 2,
      height: bottom - top + SPOT_PAD * 2,
    };
    rects.forEach((r) => {
      const ring = document.createElement('div');
      ring.className = 'tut-spot';
      ring.style.left = Math.round(r.left - ovr.left - SPOT_PAD) + 'px';
      ring.style.top = Math.round(r.top - ovr.top - SPOT_PAD) + 'px';
      ring.style.width = Math.round(r.width + SPOT_PAD * 2) + 'px';
      ring.style.height = Math.round(r.height + SPOT_PAD * 2) + 'px';
      layer.appendChild(ring);
    });
    const { card, arrow } = buildCallout(c, i + 1, i === 0);
    layer.appendChild(card);
    pending.push({ card, arrow, spot, dir: c.arrow });
  });

  // Position after every card is in the DOM so offsetWidth/Height are real.
  pending.forEach((p) => placeCard(p.card, p.arrow, p.spot, p.dir, bounds, placed));
}

function onResize(): void {
  if (openGame) paint(openGame);
}

/** Open the instructions for a game. Returns false when there is nothing to
    show (no overlay mounted / no targets on screen) so callers can tell. */
export function showTutorial(game: TutorialGameKey, source: 'auto' | 'info' = 'info'): boolean {
  const ov = document.getElementById('tutorialOverlay');
  if (!ov) return false;
  if (resolveCallouts(game).length === 0) return false;
  openGame = game;
  ov.classList.remove('hidden');
  ov.setAttribute('aria-hidden', 'false');
  paint(game);
  // A second paint on the next frame catches screens still settling their
  // layout (the wheel rebuilds its SVG as it is shown).
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      if (openGame === game) paint(game);
    });
  }
  try {
    window.addEventListener('resize', onResize);
  } catch {
    /* no window — nothing to keep in sync */
  }
  track('TutorialShown', { game, source });
  return true;
}

export function hideTutorial(): void {
  openGame = null;
  const ov = document.getElementById('tutorialOverlay');
  if (!ov) return;
  ov.classList.add('hidden');
  ov.setAttribute('aria-hidden', 'true');
  const layer = document.getElementById('tutorialLayer');
  if (layer) layer.innerHTML = '';
  try {
    window.removeEventListener('resize', onResize);
  } catch {
    /* noop */
  }
}

export function isTutorialOpen(): boolean {
  return openGame !== null;
}

/** FIRST-encounter auto-show, called from each showX(). No-ops forever after
    the first time; the (i) button is the way back in. */
export function maybeShowTutorial(game: TutorialGameKey): boolean {
  if (hasSeenTutorial(game)) return false;
  if (typeof document === 'undefined' || !document.getElementById('tutorialOverlay')) return false;
  if (resolveCallouts(game).length === 0) return false;
  markTutorialSeen(game);
  // Render on the next frame: showX() has only just unhidden the screen, so the
  // real elements have no measurable geometry yet this tick.
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => showTutorial(game, 'auto'));
  else showTutorial(game, 'auto');
  return true;
}

/** Wire once at boot: the (i) buttons, the ✕, and tap-anywhere-to-dismiss. */
export function initTutorials(): void {
  probeTheoArt();
  document.querySelectorAll<HTMLElement>('[data-tut]').forEach((b) => {
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      showTutorial(b.dataset.tut as TutorialGameKey, 'info');
    });
  });
  const ov = document.getElementById('tutorialOverlay');
  if (!ov) return;
  ov.addEventListener('pointerdown', (e) => {
    e.preventDefault(); // dismissible from the very first frame, anywhere
    hideTutorial();
  });
  const x = document.getElementById('tutorialClose');
  if (x) x.addEventListener('click', () => hideTutorial());
  // Keyboard parity, captured BEFORE main.ts's global handler so Enter can't
  // spin the wheel / start a board that the player can't even see yet.
  document.addEventListener(
    'keydown',
    (e) => {
      if (!openGame) return;
      e.stopPropagation();
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        hideTutorial();
      }
    },
    true,
  );
}
